var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { Transition as HeadlessTransition } from '@headlessui/react';
import { Input } from './Input';
import NoResults from './NoResults';
import ResultItem from './ResultItem';
import mergeClassnames from '../../../mergeClassnames/mergeClassnames';
import useClickOutside from '../../../private/hooks/useClickOutside';
import { SearchContext, SelectContext } from '../utils/context';
const SearchRoot = ({ selected: selectedParent, onChangeSelected, onChangeSearch, onChangeOpen, children, isOpen, className, search, }) => {
    const [ref, hasClickedOutside] = useClickOutside();
    const inputRef = useRef(null);
    const [selected, setSelected] = typeof selectedParent === 'number' && onChangeSelected
        ? [selectedParent, onChangeSelected]
        : useState(0);
    function handleChangeSelected(direction) {
        const items = document.querySelectorAll('.moon-search-list-item');
        let index = 0;
        let newIndex = 0;
        let newItem;
        if (direction === 'down') {
            items.forEach((_, i) => {
                if (i === selected) {
                    index = i;
                }
            });
            newIndex = index === items.length - 1 ? 0 : index + 1;
        }
        else if (direction === 'up') {
            items.forEach((_, i) => {
                if (i === selected) {
                    index = i;
                }
            });
            newIndex = !index ? items.length - 1 : index - 1;
        }
        else {
            setSelected(0);
        }
        newItem = items[newIndex];
        if (newItem && typeof newIndex === 'number') {
            setSelected(newIndex);
            newItem.scrollIntoView({
                behavior: 'smooth',
                block: newIndex ? 'center' : 'end',
            });
        }
    }
    function handleSelect() {
        var _a;
        const items = document.querySelectorAll('.moon-search-list-item');
        let index = 0;
        let item;
        items.forEach((_, i) => {
            if (i === selected) {
                index = i;
            }
        });
        item = items[index];
        if (item) {
            item.click();
            if (((_a = item.attributes.getNamedItem('data-close-on-select')) === null || _a === void 0 ? void 0 : _a.value) === 'true') {
                onChangeOpen(false);
            }
        }
    }
    useEffect(() => {
        handleChangeSelected();
    }, [search]);
    const onKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown' ||
            (e.ctrlKey && e.key === 'n') ||
            (e.ctrlKey && e.key === 'j')) {
            e.preventDefault();
            e.stopPropagation();
            handleChangeSelected('down');
        }
        else if (e.key === 'ArrowUp' ||
            (e.ctrlKey && e.key === 'p') ||
            (e.ctrlKey && e.key === 'k')) {
            e.preventDefault();
            e.stopPropagation();
            handleChangeSelected('up');
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            handleSelect();
        }
    }, [handleChangeSelected, handleSelect]);
    React.useEffect(() => {
        if (hasClickedOutside) {
            onChangeOpen(false);
        }
    });
    const openSearch = useCallback(() => onChangeOpen(true), []);
    return (React.createElement("div", { ref: ref, onKeyDown: onKeyDown, onClick: openSearch },
        React.createElement("div", { className: mergeClassnames('relative w-full h-full bg-goku flex flex-col border border-beerus', isOpen ? 'rounded-t-moon-s-sm' : 'rounded-moon-s-sm', '[&_.moon-search-result]:top-10', '[&_.moon-search-transition>.moon-search-result]:top-0', className) },
            React.createElement(SearchContext.Provider, { value: { search, onChangeOpen, onChangeSearch, inputRef, isOpen } },
                React.createElement(SelectContext.Provider, { value: { selected } }, children)))));
};
const Transition = ({ children, className, }) => {
    const { isOpen } = useContext(SearchContext);
    return (React.createElement(HeadlessTransition, { show: isOpen, as: "div", enter: "ease-out duration-300", enterFrom: "opacity-0 scale-95", enterTo: "opacity-100 scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 scale-100", leaveTo: "opacity-0 scale-95", className: mergeClassnames('moon-search-transition z-5', className) }, children));
};
const Result = (_a) => {
    var { children, className } = _a, props = __rest(_a, ["children", "className"]);
    const { isOpen } = useContext(SearchContext);
    if (!isOpen) {
        return null;
    }
    return (React.createElement("div", Object.assign({ className: mergeClassnames('moon-search-result', 'absolute w-full flex-1 focus:outline-none p-2 space-y-4 bg-goku shadow-moon-md ', isOpen ? 'rounded-b-moon-s-sm' : 'rounded-moon-s-sm', className), tabIndex: -1 }, props), children));
};
const ResultHeading = (_a) => {
    var { children, className } = _a, props = __rest(_a, ["children", "className"]);
    return (React.createElement("h5", Object.assign({ className: mergeClassnames('text-bulma text-moon-14 font-medium px-2 py-1', className) }, props), children));
};
const Search = Object.assign(SearchRoot, {
    Input,
    NoResults,
    Transition,
    Result,
    ResultItem,
    ResultHeading,
});
export default Search;
